import { baseApi } from "@/store/api/base-api";
import { ApiEndpoints, buildEndpoint } from "@_constants/endpoints";
import type {
  ReorderFiltersPayload,
  CreateFilterRequest,
  FilterResponse,
  QuestionResponsesSearchRequest,
  QuestionResponsesSearchResponse,
} from "@models/Filter";

export const filtersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFilterById: builder.query<
      FilterResponse,
      { dashboardId: string; filterId: string }
    >({
      query: ({
        dashboardId,
        filterId,
      }: {
        dashboardId: string;
        filterId: string;
      }) => ({
        url: `${buildEndpoint(ApiEndpoints.DASHBOARD_FILTERS, { dashboardId })}/${filterId}`,
        method: "GET",
      }),
      providesTags: (
        _: unknown,
        __: unknown,
        { dashboardId, filterId }: { dashboardId: string; filterId: string },
      ) => [{ type: "Dashboard", id: `${dashboardId}-filter-${filterId}` }],
    }),
    updateFilter: builder.mutation<
      FilterResponse,
      {
        dashboardId: string;
        filterId: string;
        body: Partial<CreateFilterRequest>;
      }
    >({
      query: ({
        dashboardId,
        filterId,
        body,
      }: {
        dashboardId: string;
        filterId: string;
        body: Partial<CreateFilterRequest>;
      }) => ({
        url: `${buildEndpoint(ApiEndpoints.DASHBOARD_FILTERS, { dashboardId })}/${filterId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (
        _: unknown,
        __: unknown,
        { dashboardId, filterId }: { dashboardId: string; filterId: string },
      ) => [
        { type: "Dashboard", id: `${dashboardId}-filters` },
        { type: "Dashboard", id: `${dashboardId}-filter-${filterId}` },
      ],
    }),
    createFilter: builder.mutation<
      FilterResponse,
      { dashboardId: string; body: CreateFilterRequest }
    >({
      query: ({
        dashboardId,
        body,
      }: {
        dashboardId: string;
        body: CreateFilterRequest;
      }) => ({
        url: buildEndpoint(ApiEndpoints.DASHBOARD_FILTERS, { dashboardId }),
        method: "POST",
        body,
      }),
      invalidatesTags: (
        _: unknown,
        __: unknown,
        { dashboardId }: { dashboardId: string; body: CreateFilterRequest },
      ) => [{ type: "Dashboard", id: `${dashboardId}-filters` }],
    }),
    deleteFilter: builder.mutation<
      void,
      { dashboardId: string; filterId: string }
    >({
      query: ({
        dashboardId,
        filterId,
      }: {
        dashboardId: string;
        filterId: string;
      }) => ({
        url: `${buildEndpoint(ApiEndpoints.DASHBOARD_FILTERS, { dashboardId })}/${filterId}`,
        method: "DELETE",
      }),
      invalidatesTags: (
        _: unknown,
        __: unknown,
        { dashboardId }: { dashboardId: string; filterId: string },
      ) => [{ type: "Dashboard", id: `${dashboardId}-filters` }],
    }),
    reorderFilters: builder.mutation<void, ReorderFiltersPayload>({
      query: ({ dashboardId, filters }: ReorderFiltersPayload) => ({
        url: `${buildEndpoint(ApiEndpoints.DASHBOARD_FILTERS, { dashboardId })}/reorder`,
        method: "PATCH",
        body: { filters },
      }),
      invalidatesTags: (
        _: unknown,
        __: unknown,
        { dashboardId }: ReorderFiltersPayload,
      ) => [{ type: "Dashboard", id: `${dashboardId}-filters` }],
    }),
    searchQuestionResponses: builder.mutation<
      QuestionResponsesSearchResponse,
      QuestionResponsesSearchRequest
    >({
      query: (body: QuestionResponsesSearchRequest) => ({
        url: ApiEndpoints.AGGREGATION_QUESTIONS_RESPONSES_SEARCH,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useCreateFilterMutation,
  useDeleteFilterMutation,
  useReorderFiltersMutation,
  useLazyGetFilterByIdQuery,
  useUpdateFilterMutation,
  useSearchQuestionResponsesMutation,
} = filtersApi;

export default filtersApi;
